import React, { useEffect } from "react";
import Loader from "../components/common/loader/loader";
import { TableColumn } from "../interfaces/tables";
import Table from "../components/ui/table";
import { MdDelete, MdEdit } from "react-icons/md";
import { Button } from "../components/ui/button";
import Error from "../components/common/error/error";
import TableHeaders from "../components/ui/table-headers";
import { siteSettings } from "../settings/site-settings";
import { notify } from "../lib/utils";
import usePageTitle from "../hooks/usePageTitle";
import {
  deletePrintLocation,
  listAllPrintLocations,
} from "../api/admin/print-locations";
import CreateUpdatePrintLocationDialog from "../components/print-locations/create-update-print-location-dialog";
import DeletePrintLocationDialog from "../components/print-locations/delete-print-location-dialog";

function PrintLocations() {
  usePageTitle("Print Locations");
  const [printLocations, setPrintLocations] = React.useState<any>([]);
  const [tableData, setTableData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  // table headers state, search, sort, page.
  const [sort, setSort] = React.useState<any>({ createdAt: -1 });
  const [search, setSearch] = React.useState(null);

  const [page, setPage] = React.useState(1);

  const [pageSize, setPageSize] = React.useState(
    siteSettings.tables.projects.defaultPageSize.key
  );

  const [paginationTotalCount, setPaginationTotalCount] = React.useState(0);

  // regenerates the api key for the project
  const [selectedPrintLocationForUpdate, setSelectedPrintLocationForUpdate] =
    React.useState<any | null>(null);

  const [printLocationUpdateDialogOpen, setPrintLocationUpdateDialogOpen] =
    React.useState(false);

  // if this true, the modal will be for edit, else for create.
  const [isPrintLocationBeingUpdated, setIsPrintLocationBeingUpdated] =
    React.useState(false);

  const [selectedPrintLocationForDelete, setSelectedPrintLocationForDelete] =
    React.useState<any | null>(null);

  const [printLocationsDeleteDialogOpen, setPrintLocationDeleteDialogOpen] =
    React.useState(false);

  const handleOpenDeletePrintLocationDialog = (printLocation: any) => {
    setSelectedPrintLocationForDelete(printLocation);
    setPrintLocationDeleteDialogOpen(true);
  };

  const handleCloseDeletePrintLocationDialog = () => {
    setSelectedPrintLocationForDelete(null);
    setPrintLocationDeleteDialogOpen(false);
  };

  const handleOpenUpdatePrintLocationDialog = (printLocation: any) => {
    setSelectedPrintLocationForUpdate(printLocation);
    setIsPrintLocationBeingUpdated(true);
    setPrintLocationUpdateDialogOpen(true);
  };

  const handleCloseUpdatePrintLocationDialog = () => {
    setSelectedPrintLocationForUpdate(null);
    setIsPrintLocationBeingUpdated(false);
    setPrintLocationUpdateDialogOpen(false);
  };

  const handleOnPrintLocationUpdated = () => {
    fetchPrintLocations();
  };

  const fetchPrintLocations = async () => {
    const response = await listAllPrintLocations({
      page,
      sort,
      search,
      pageSize,
    });

    if (!response || response.status !== 200) {
      console.log("Error fetching projects");
      setError(true);
      setLoading(false);

      return;
    }

    setPrintLocations(response.data?.data);
    setTableData(formatDataForTable(response.data?.data));
    setPaginationTotalCount(response.data?.total);
    setLoading(false);
  };

  const handlePrintLocationDelete = async (printLocationId: string) => {
    const response = await deletePrintLocation(printLocationId);

    if (!response || response.status !== 200) {
      notify({
        type: "error",
        message: "Error deleting Print Location",
      });
      return;
    }

    notify({
      type: "success",
      message: "Print Location deleted successfully",
    });

    setPrintLocationDeleteDialogOpen(false);
    fetchPrintLocations();
  };

  const handleOnSearchChange = (searchObj: any) => {
    if (!searchObj || !searchObj.value || searchObj.value === "") {
      setSearch(null);
      return;
    }

    setSearch(searchObj);
    setPage(1); // reset page to 1 when search changes
  };

  const handleOnSortChange = (sortObj: any) => {
    if (!sortObj) {
      setSort(null);
      return;
    }

    setSort(sortObj);
  };

  const handleOnPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
  };

  // headers change listener
  useEffect(() => {
    fetchPrintLocations();
  }, [page, sort, search, pageSize]);

  const columns: TableColumn[] = [
    { key: "_id", label: "Internal Id" },
    { key: "internalName", label: "Internal Name" },
    { key: "slug", label: "Slug" },

    {
      key: "createdAt",
      label: "Created At",
      render: (cell) => {
        return cell ? new Date(cell).toLocaleDateString() : "N/A";
      },
    },
    {
      key: "updatedAt",
      label: "Updated At",
      render: (cell) => {
        return cell ? new Date(cell).toLocaleDateString() : "N/A";
      },
    },

    {
      key: "btn",
      label: "Actions",
      fixed: true,
      render: (cell, row) => {
        return (
          <div className="flex gap-x-4">
            <MdEdit
              title="Edit"
              className="hover:text-primary cursor-pointer"
              size={24}
              onClick={() => {
                handleOpenUpdatePrintLocationDialog(row);
              }}
            />

            <MdDelete
              title="Delete"
              className="hover:text-red-600 cursor-pointer"
              size={24}
              onClick={() => {
                handleOpenDeletePrintLocationDialog(row);
              }}
            />
          </div>
        );
      },
    },
  ];

  const formatDataForTable = (data: any) => {
    return data.map((d: any) => {
      return {
        _id: d._id,
        internalName: d.internalName,
        slug: d.slug,
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
        btn: d._id,
        fullDoc: d,
      };
    });
  };

  return (
    <div className="w-full h-full">
      {loading && <Loader pageLoader />}

      {!loading && error && <Error />}

      {!loading && !error && (
        <div className="w-[95%] mx-auto ">
          <div className="w-full mx-auto">
            <h1 className="text-2xl font-bold text-primary">Print Locations</h1>
            {paginationTotalCount && paginationTotalCount > 0 && (
              <p className="text-bodyDark">Total: {paginationTotalCount}</p>
            )}
          </div>

          <div className="w-full mx-auto flex items-end justify-end">
            <Button
              onClick={() => {
                setIsPrintLocationBeingUpdated(false);
                setPrintLocationUpdateDialogOpen(true);
              }}
            >
              Create Print Location
            </Button>
          </div>

          <div className="w-full block  mx-auto my-10 pb-20">
            <TableHeaders
              searchByOptions={
                siteSettings.tables.printLocations.searchByOptions
              }
              onSearchChange={handleOnSearchChange}
              sortByOptions={siteSettings.tables.printLocations.sortByOptions}
              onSortChange={handleOnSortChange}
              page={page}
              onPageChange={setPage}
              lastPage={
                paginationTotalCount === 0
                  ? 1
                  : Math.ceil(paginationTotalCount / pageSize)
              }
              pageSizeOptions={
                siteSettings.tables.printLocations.pageSizeOptions
              }
              onPageSizeChange={handleOnPageSizeChange}
              defaultPageSize={
                siteSettings.tables.printLocations.defaultPageSize
              }
            />
            <Table columns={columns} data={tableData} />
          </div>

          <CreateUpdatePrintLocationDialog
            printLocation={selectedPrintLocationForUpdate}
            open={printLocationUpdateDialogOpen}
            onClose={handleCloseUpdatePrintLocationDialog}
            onPrintLocationUpdated={handleOnPrintLocationUpdated}
            isEditMode={isPrintLocationBeingUpdated}
          />

          <DeletePrintLocationDialog
            printLocation={selectedPrintLocationForDelete}
            open={printLocationsDeleteDialogOpen}
            onClose={handleCloseDeletePrintLocationDialog}
            onPrintLocationDelete={(printLocationId: string) => {
              handlePrintLocationDelete(printLocationId);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default PrintLocations;
